// src/firebase/strategyService.js
//
// "Strategie": preset personali (etichette, fasce livello, note sui
// giocatori) salvati per account, indipendenti da una singola asta. Da
// un'asta si può importare una strategia: i valori vengono copiati dentro
// l'asta corrente (playerCategories/playerTiers/playerNotes), sovrascrivendo
// quelli già presenti per i giocatori coperti dalla strategia. Da quel
// momento in poi le due cose vivono separate: modifiche fatte in asta dopo
// l'import non toccano più la strategia salvata.
//
// Visibilità: una strategia è privata di default (leggibile solo dal
// proprietario, via la regola generale users/{uid}/{document=**}) oppure
// pubblica (leggibile da chiunque sia autenticato, per poterla importare in
// una propria asta — vedi la regola dedicata in firestore.rules). Le
// strategie pubbliche vengono anche specchiate in un registro top-level
// (publicStrategies) con solo nome/descrizione, così si possono sfogliare
// senza conoscere l'uid del proprietario — stesso pattern già usato per le
// aste pubbliche (vedi publicAuctions in auctionService.js).
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { normalizePlayerCategory, normalizePlayerTier } from './auctionService';

const USERS_COLLECTION = 'users';
const STRATEGIES_COLLECTION = 'strategies';
const PUBLIC_STRATEGIES_COLLECTION = 'publicStrategies';
// Condivisione mirata: registro top-level delle strategie condivise con
// utenti specifici (non pubbliche). Serve per la query "condivise con me"
// (`sharedWith` array-contains il proprio uid) — non si possono scandire le
// sottocollezioni strategies di tutti gli utenti. Tenuto in sincronia dal
// proprietario a ogni aggiunta/rimozione di una persona.
const SHARED_STRATEGIES_COLLECTION = 'sharedStrategies';
// Lookup codice → uid per la condivisione "tramite codice amico".
const SHARE_CODES_COLLECTION = 'shareCodes';
// Alfabeto senza caratteri ambigui (niente O/0/I/1) per codici leggibili.
const SHARE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const SHARE_CODE_LENGTH = 6;
const MAX_STRATEGY_SHARES = 30;

function strategiesCollectionPath(uid) {
  return collection(db, USERS_COLLECTION, uid, STRATEGIES_COLLECTION);
}

function strategyDocPath(uid, strategyId) {
  return doc(db, USERS_COLLECTION, uid, STRATEGIES_COLLECTION, strategyId);
}

function sharedStrategyDocPath(strategyId) {
  return doc(db, SHARED_STRATEGIES_COLLECTION, strategyId);
}

function shareCodeDocPath(code) {
  return doc(db, SHARE_CODES_COLLECTION, code);
}

function normalizeShareCodeInput(value = '') {
  return String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, SHARE_CODE_LENGTH);
}

function randomShareCode() {
  let out = '';
  for (let i = 0; i < SHARE_CODE_LENGTH; i += 1) {
    out += SHARE_CODE_ALPHABET[Math.floor(Math.random() * SHARE_CODE_ALPHABET.length)];
  }
  return out;
}

function normalizeVisibility(value = 'private') {
  return value === 'public' ? 'public' : 'private';
}

// Tetto di spesa personale per giocatore, come % del budget totale
// dell'asta (non un valore assoluto in crediti): una strategia può essere
// importata in aste con budget diversi (500 crediti, 1000 crediti...), la
// percentuale si traduce nel valore assoluto giusto per QUELLA asta al
// momento dell'import (vedi importStrategyIntoAuction in auctionService.js).
// 0 = nessun tetto impostato. Un decimale di precisione (es. 12.5%).
export function normalizeMaxSpendPercent(value = 0) {
  const n = Math.round(Number(value || 0) * 10) / 10;
  return Number.isFinite(n) && n > 0 ? Math.min(n, 100) : 0;
}

function normalizeRolesMode(value) {
  return value === 'mantra' ? 'mantra' : 'classic';
}

function normalizeStrategy(id, data = {}) {
  return {
    id,
    name: String(data.name || '').trim() || 'Strategia senza nome',
    description: String(data.description || ''),
    visibility: normalizeVisibility(data.visibility),
    // Modalità ruoli della strategia (classic | mantra): decide quale ruolo
    // mostrare nella tabella di StrategyEditPage e i chip del filtro ruolo.
    // Non influenza l'import in asta (che copia solo etichette/fasce/note per
    // playerId, indipendenti dal ruolo).
    rolesMode: normalizeRolesMode(data.rolesMode),
    // Condivisione mirata: uid degli utenti con cui la strategia è condivisa
    // (indipendente da visibility — una strategia privata può comunque essere
    // condivisa con persone specifiche tramite il loro codice).
    sharedWith: Array.isArray(data.sharedWith) ? data.sharedWith.map(String) : [],
    categories: data.categories || {},
    tiers: data.tiers || {},
    notes: data.notes || {},
    maxSpends: data.maxSpends || {},
    // Solo per i portieri: nome del portiere di riserva della stessa
    // squadra, indicato manualmente (vedi StrategyEditPage.js, colonna "Por
    // riserva" visibile filtrando per ruolo P). Stringa libera come le
    // note, non un id giocatore: basta per mostrarla come chip informativa
    // in asta, non serve risolverla a un player object.
    backupGoalkeepers: data.backupGoalkeepers || {},
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

export async function getUserStrategies(uid) {
  if (!uid) return [];
  const q = query(strategiesCollectionPath(uid), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalizeStrategy(d.id, d.data()));
}

export async function getStrategyById(uid, strategyId) {
  if (!uid || !strategyId) return null;
  const snap = await getDoc(strategyDocPath(uid, strategyId));
  if (!snap.exists()) return null;
  return normalizeStrategy(snap.id, snap.data());
}

// Registro pubblico: solo i campi utili a sfogliare/cercare (niente
// etichette/fasce/note, per evitare di doverle tenere sincronizzate in due
// posti). L'import legge sempre il documento sorgente aggiornato, tramite
// getStrategyById(ownerUid, strategyId), reso leggibile a tutti dalla
// regola dedicata quando visibility === 'public'.
export async function syncPublicStrategy(uid, strategy) {
  if (!uid || !strategy?.id) return false;
  await setDoc(doc(db, PUBLIC_STRATEGIES_COLLECTION, strategy.id), {
    ownerUid: uid,
    strategyId: strategy.id,
    name: strategy.name || '',
    description: strategy.description || '',
    updatedAt: Date.now(),
  });
  return true;
}

export async function removePublicStrategy(strategyId) {
  if (!strategyId) return false;
  try {
    await deleteDoc(doc(db, PUBLIC_STRATEGIES_COLLECTION, strategyId));
  } catch {
    // Ignora se non esiste
  }
  return true;
}

export async function getPublicStrategies() {
  const snap = await getDocs(collection(db, PUBLIC_STRATEGIES_COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createStrategy(uid, { name, description = '', visibility = 'private' } = {}) {
  if (!uid) return null;
  const payload = {
    name: String(name || '').trim() || 'Nuova strategia',
    description: String(description || '').trim(),
    visibility: normalizeVisibility(visibility),
    rolesMode: 'classic',
    sharedWith: [],
    categories: {},
    tiers: {},
    notes: {},
    maxSpends: {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const ref = await addDoc(strategiesCollectionPath(uid), payload);
  const strategy = normalizeStrategy(ref.id, payload);

  if (strategy.visibility === 'public') {
    await syncPublicStrategy(uid, strategy);
  }

  return strategy;
}

// `strategy` è l'oggetto corrente (serve la sua visibility per capire se
// tenere allineato anche il registro pubblico).
export async function updateStrategyMeta(uid, strategy, { name, description } = {}) {
  if (!uid || !strategy?.id) return false;
  const payload = { updatedAt: Date.now() };
  if (name !== undefined) payload.name = String(name || '').trim() || 'Strategia senza nome';
  if (description !== undefined) payload.description = String(description || '').trim();

  await updateDoc(strategyDocPath(uid, strategy.id), payload);

  if (strategy.visibility === 'public') {
    // Il registro pubblico è scrivibile solo dal proprietario: se a modificare
    // è un utente con cui la strategia è condivisa, questa sync fallisce ed è
    // ok ignorarla (il proprietario la riallinea alla sua prossima modifica).
    await syncPublicStrategy(uid, { ...strategy, ...payload }).catch(() => {});
  }

  // Se è condivisa con qualcuno, tieni allineati nome/descrizione anche nel
  // registro `sharedStrategies` (quello che vede chi la riceve).
  if (Array.isArray(strategy.sharedWith) && strategy.sharedWith.length) {
    const mirror = {};
    if (name !== undefined) mirror.name = payload.name;
    if (description !== undefined) mirror.description = payload.description;
    if (Object.keys(mirror).length) {
      mirror.updatedAt = Date.now();
      await setDoc(sharedStrategyDocPath(strategy.id), mirror, { merge: true }).catch(() => {});
    }
  }

  return true;
}

export async function setStrategyRolesMode(uid, strategyId, rolesMode) {
  if (!uid || !strategyId) return false;
  await updateDoc(strategyDocPath(uid, strategyId), {
    rolesMode: normalizeRolesMode(rolesMode),
    updatedAt: Date.now(),
  });
  return true;
}

export async function setStrategyVisibility(uid, strategy, nextVisibility) {
  if (!uid || !strategy?.id) return false;
  const visibility = normalizeVisibility(nextVisibility);

  await updateDoc(strategyDocPath(uid, strategy.id), { visibility, updatedAt: Date.now() });

  if (visibility === 'public') {
    await syncPublicStrategy(uid, { ...strategy, visibility });
  } else {
    await removePublicStrategy(strategy.id);
  }

  return true;
}

export async function deleteStrategy(uid, strategyId) {
  if (!uid || !strategyId) return false;
  await deleteDoc(strategyDocPath(uid, strategyId));
  await removePublicStrategy(strategyId);
  // Rimuove anche il registro condivisione (chi la riceveva non la vede più).
  await deleteDoc(sharedStrategyDocPath(strategyId)).catch(() => {});
  return true;
}

// ─── Condivisione mirata: codice personale + persone autorizzate ─────────────

// Codice personale dell'utente (6 caratteri) da dare agli amici perché possano
// essere aggiunti alle proprie strategie condivise. Generato una sola volta e
// riusato; salvato su users/{uid}.shareCode e nel lookup shareCodes/{CODE}.
export async function getOrCreateMyShareCode(uid) {
  if (!uid) return null;

  const userRef = doc(db, USERS_COLLECTION, uid);
  const userSnap = await getDoc(userRef).catch(() => null);
  const existing = normalizeShareCodeInput(userSnap?.exists() ? userSnap.data().shareCode : '');
  if (existing.length === SHARE_CODE_LENGTH) {
    // Assicura che il lookup esista (potrebbe mancare per dati vecchi).
    await setDoc(shareCodeDocPath(existing), { uid, updatedAt: Date.now() }, { merge: true }).catch(() => {});
    return existing;
  }

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const code = randomShareCode();
    try {
      // eslint-disable-next-line no-await-in-loop
      const claimed = await runTransaction(db, async (tx) => {
        const codeRef = shareCodeDocPath(code);
        const codeSnap = await tx.get(codeRef);
        if (codeSnap.exists() && String(codeSnap.data().uid) !== String(uid)) {
          return false; // collisione: riprova con un altro codice
        }
        tx.set(codeRef, { uid, updatedAt: Date.now() }, { merge: true });
        tx.set(userRef, { shareCode: code, updatedAt: Date.now() }, { merge: true });
        return true;
      });
      if (claimed) return code;
    } catch {
      // riprova
    }
  }
  return null;
}

// Risolve un codice amico nell'uid corrispondente (o null se non valido).
export async function resolveShareCode(code) {
  const normalized = normalizeShareCodeInput(code);
  if (normalized.length !== SHARE_CODE_LENGTH) return null;
  const snap = await getDoc(shareCodeDocPath(normalized)).catch(() => null);
  if (!snap || !snap.exists()) return null;
  const targetUid = String(snap.data().uid || '');
  return targetUid || null;
}

// Aggiunge una persona (per uid già risolto) all'elenco di chi può vedere e
// importare la strategia. Aggiorna sia il documento strategia sia il registro
// `sharedStrategies`. Ritorna il nuovo array `sharedWith`.
export async function addStrategyShareByUid(uid, strategy, granteeUid) {
  if (!uid || !strategy?.id || !granteeUid) return null;
  const target = String(granteeUid);
  if (target === String(uid)) throw new Error('own-uid');

  const current = Array.isArray(strategy.sharedWith) ? strategy.sharedWith.map(String) : [];
  const alreadyShared = current.includes(target);
  if (!alreadyShared && current.length >= MAX_STRATEGY_SHARES) throw new Error('limit');

  const next = alreadyShared ? current : [...current, target];

  // Riscrive SEMPRE il registro `sharedStrategies` — anche se la persona
  // risultava già in `sharedWith` sul documento strategia. Serve a riparare le
  // condivisioni fatte prima che il registro/regole esistessero (allora
  // `sharedWith` veniva aggiornato ma il registro no): senza questo, ri-aggiungere
  // la stessa persona non produceva alcun effetto. È il documento che il
  // destinatario interroga, quindi va scritto per primo: se fallisce (es. regole
  // non deployate) l'errore risale prima di toccare il documento strategia.
  await setDoc(
    sharedStrategyDocPath(strategy.id),
    {
      ownerUid: uid,
      strategyId: strategy.id,
      name: strategy.name || 'Strategia senza nome',
      description: strategy.description || '',
      rolesMode: normalizeRolesMode(strategy.rolesMode),
      sharedWith: next,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  if (!alreadyShared) {
    await updateDoc(strategyDocPath(uid, strategy.id), {
      sharedWith: arrayUnion(target),
      updatedAt: Date.now(),
    });
  }

  return next;
}

// Rimuove una persona. Se non resta nessuno, cancella anche il registro.
export async function removeStrategyShareByUid(uid, strategy, granteeUid) {
  if (!uid || !strategy?.id || !granteeUid) return null;
  const target = String(granteeUid);
  const current = Array.isArray(strategy.sharedWith) ? strategy.sharedWith.map(String) : [];
  const next = current.filter((x) => x !== target);

  await updateDoc(strategyDocPath(uid, strategy.id), {
    sharedWith: arrayRemove(target),
    updatedAt: Date.now(),
  });

  if (next.length) {
    await setDoc(
      sharedStrategyDocPath(strategy.id),
      { sharedWith: next, updatedAt: Date.now() },
      { merge: true }
    ).catch(() => {});
  } else {
    await deleteDoc(sharedStrategyDocPath(strategy.id)).catch(() => {});
  }

  return next;
}

// Strategie che ALTRI hanno condiviso con me (registro sharedStrategies dove il
// mio uid è in `sharedWith`). Solo metadati: il documento sorgente si legge poi
// con getStrategyById(ownerUid, strategyId) al momento dell'import.
export async function getStrategiesSharedWithMe(uid) {
  if (!uid) return [];
  const q = query(
    collection(db, SHARED_STRATEGIES_COLLECTION),
    where('sharedWith', 'array-contains', String(uid))
  );
  let snap = null;
  try {
    snap = await getDocs(q);
  } catch (err) {
    // Utile per capire se è un problema di regole (permission-denied) o di
    // indice mancante piuttosto che "nessuna strategia condivisa".
    console.warn('[strategyService] getStrategiesSharedWithMe fallita:', err?.code || err?.message || err);
    return [];
  }
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  console.debug('[strategyService] sharedStrategies trovate per', uid, '→', all.length, all);
  return all.filter((entry) => String(entry.ownerUid) !== String(uid));
}

export async function saveStrategyPlayerCategory(uid, strategyId, playerId, category) {
  if (!uid || !strategyId || !playerId) return false;
  const normalized = normalizePlayerCategory(category);
  await updateDoc(strategyDocPath(uid, strategyId), {
    [`categories.${playerId}`]: normalized === 'none' ? deleteField() : normalized,
    updatedAt: Date.now(),
  });
  return true;
}

export async function saveStrategyPlayerTier(uid, strategyId, playerId, tier) {
  if (!uid || !strategyId || !playerId) return false;
  const normalized = normalizePlayerTier(tier);
  await updateDoc(strategyDocPath(uid, strategyId), {
    [`tiers.${playerId}`]: normalized === 0 ? deleteField() : normalized,
    updatedAt: Date.now(),
  });
  return true;
}

export async function saveStrategyPlayerMaxSpend(uid, strategyId, playerId, percent) {
  if (!uid || !strategyId || !playerId) return false;
  const normalized = normalizeMaxSpendPercent(percent);
  await updateDoc(strategyDocPath(uid, strategyId), {
    [`maxSpends.${playerId}`]: normalized === 0 ? deleteField() : normalized,
    updatedAt: Date.now(),
  });
  return true;
}

export async function saveStrategyPlayerNote(uid, strategyId, playerId, note) {
  if (!uid || !strategyId || !playerId) return false;
  const normalized = String(note || '').trim();
  await updateDoc(strategyDocPath(uid, strategyId), {
    [`notes.${playerId}`]: normalized ? normalized : deleteField(),
    updatedAt: Date.now(),
  });
  return true;
}

export async function saveStrategyPlayerBackupGoalkeeper(uid, strategyId, playerId, backupName) {
  if (!uid || !strategyId || !playerId) return false;
  const normalized = String(backupName || '').trim();
  await updateDoc(strategyDocPath(uid, strategyId), {
    [`backupGoalkeepers.${playerId}`]: normalized ? normalized : deleteField(),
    updatedAt: Date.now(),
  });
  return true;
}
